import { Component, prop } from "@kluntje/core";
import { fetchJSON } from "Helper/apiHelper";
import { I18nService } from "Services/I18nService";
import { render } from "lit-html";
import {
  dbLink,
  departuresListTemplate,
  loadMoreDeparturesButtonTemplate,
  mapLinkTemplate,
  noDeparturesAvailable,
  // eslint-disable-next-line import/extensions
} from "Components/fra-m-train-departures/fra-m-train-departures__list.template";
import { addClass, hasClass, removeClass } from "@kluntje/js-utils/lib/dom-helpers";
import { HIDDEN } from "Helper/cssClasses";

class FRATrainDepartures extends Component {
  @prop({ required: true })
  dataApiUrl = "";

  @prop({ required: true })
  mapLinkRegio = "";

  @prop({ required: true })
  mapLinkFern = "";

  /**
   * time is used as startTime for api call. If not provided, current time is used
   * @type {"string"}
   */
  @prop({ required: false })
  time = "";

  constructor() {
    super({
      ui: {
        mapLink: ".fra-m-train-departures__map-link:-one",
        dbLink: ".fra-m-train-departures__db-link:-one",
        toggle: ".fra-m-train-departures__train-station-toggle:-one",
        departuresList: ".fra-m-train-departures__list:-one",
        loadMoreDeparturesButton: ".fra-m-train-departures__load-more-button:-one",
      },
      events: [
        {
          event: "click",
          target: "toggle",
          handler: "toggleTrainStation",
        },
      ],
      initialStates: {
        departures: [],
        showDeparturesUpTo: 4,
        station: "fern",
        time: "",
      },
      reactions: {
        departures: ["handleDataUpdate"],
        time: ["fetchTrainDepartures"],
        station: ["renderMapLink", "fetchTrainDepartures"],
        showDeparturesUpTo: ["handleDataUpdate"],
      },
    });
  }

  /**
   * @override
   */
  afterComponentRender() {
    this.renderMapLink();
    this.setTime();
    this.setState({ station: hasClass(this.ui.toggle, "fra-e-toggle--default-second") ? "regio" : "fern" });
  }

  async renderMapLink(): Promise<void> {
    await I18nService.getInstance().waitForJson();
    const mapLink = this.state.station === "fern" ? this.mapLinkFern : this.mapLinkRegio;
    render(mapLinkTemplate(mapLink, this.state.station), this.ui.mapLink);
  }

  /**
   * fetch json
   * @returns {Promise<void>}
   */
  async fetchTrainDepartures(): Promise<void> {
    await I18nService.getInstance().waitForJson();
    const stationId = this.state.station === "fern" ? "8070003" : "8070004";
    const parameterizedUrl = this.dataApiUrl.replace("START_TIME", this.state.time).replace("STATION", stationId);
    const apiResponse = await fetchJSON(parameterizedUrl).catch(() => {
      render(noDeparturesAvailable(), this.ui.departuresList);
    });
    if (!apiResponse?.departures?.length) {
      render(noDeparturesAvailable(), this.ui.departuresList);
    } else {
      this.setState({ departures: apiResponse.departures.slice(0, 8) });
    }
  }

  /**
   * Reaction on state change (e.g. json fetched)
   */
  handleDataUpdate(): void {
    this.toggleLoadMoreDeparturesButton();
    this.renderDeparturesList();
  }

  async toggleLoadMoreDeparturesButton(): Promise<void> {
    await I18nService.getInstance().waitForJson();
    if (this.state.showDeparturesUpTo > 4 || (this.state.departures.length < 5 && this.state.departures.length > 0)) {
      addClass(this.ui.loadMoreDeparturesButton, HIDDEN);
      removeClass(this.ui.dbLink, HIDDEN);
      // generates db link with starting time of last visible train
      render(dbLink(this.state.departures.at(-1).time, this.state.station), this.ui.dbLink);
    } else {
      addClass(this.ui.dbLink, HIDDEN);
      removeClass(this.ui.loadMoreDeparturesButton, HIDDEN);
      render(loadMoreDeparturesButtonTemplate(this.setState.bind(this)), this.ui.loadMoreDeparturesButton);
    }
  }

  async renderDeparturesList(): Promise<void> {
    await I18nService.getInstance().waitForJson();
    const departuresList = departuresListTemplate(this.state.departures, this.state.showDeparturesUpTo, this.state);
    render(departuresList, this.ui.departuresList);
  }

  toggleTrainStation(): void {
    this.setState({ showDeparturesUpTo: 4 });
    if (this.state.station === "fern") {
      this.setState({ station: "regio" });
    } else {
      this.setState({ station: "fern" });
    }
  }

  setTime(): void {
    if (this.time) {
      this.setState({ time: this.time });
    } else {
      const currentDateTime = new Date();
      const minutes = currentDateTime.getMinutes();
      const hours = currentDateTime.getHours();
      const roundedMinutes = ((((minutes + 7.5) / 15) | 0) * 15) % 60;
      const roundedHours = (((minutes / 105 + 0.5) | 0) + hours) % 24;
      currentDateTime.setHours(roundedHours);
      currentDateTime.setMinutes(roundedMinutes);
      const currentTime = currentDateTime.toISOString().replace(/T\d\d/, `T${currentDateTime.getHours().toString()}`);
      this.setState({ time: currentTime });
    }
  }
}

customElements.define("fra-m-train-departures", FRATrainDepartures);
export default FRATrainDepartures;
